import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Moon, LogOut, Users, Trash2, Crown, ArrowLeft, MessageSquare, FileText, Plus, Edit3, Eye, EyeOff, Sparkles, Wand2, Loader2, Languages, ImageIcon, Star, MessageCircle as FeedbackIcon, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LanguageSelector from "@/components/LanguageSelector";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("users");
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: "", content: "", excerpt: "", cover_image: "", tags: "", language: "fi", is_published: false
  });
  const [aiGenerating, setAiGenerating] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchLanguages, setBatchLanguages] = useState(["fi", "en", "fr", "de", "es"]);
  const [batchCount, setBatchCount] = useState(3);
  const [aiTopic, setAiTopic] = useState("");
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, currentTitle: "" });
  const [circles, setCircles] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [circleDreams, setCircleDreams] = useState([]);
  const [translating, setTranslating] = useState(null);
  const [trialCount, setTrialCount] = useState(0);
  const [feedback, setFeedback] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({ total: 0, average_rating: 0, by_rating: {} });
  const [feedbackFilter, setFeedbackFilter] = useState("all");
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [stylesGenerating, setStylesGenerating] = useState(false);
  const [stylesResults, setStylesResults] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    type: "update",
    title: "",
    message: "",
    cta_text: "",
    cta_url: "",
    language: "fi",
    is_active: true,
  });

  useEffect(() => {
    fetchUsers();
    fetchComments();
    fetchBlogPosts();
    fetchCircles();
    fetchTrialCount();
    fetchFeedback();
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/announcements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnnouncements(res.data.items || []);
    } catch (e) {
      console.error("Failed to fetch announcements");
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    if (!announcementForm.title.trim() || !announcementForm.message.trim()) {
      toast.error("Otsikko ja viesti ovat pakollisia");
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/announcements`, announcementForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ilmoitus julkaistu!");
      setShowAnnouncementForm(false);
      setAnnouncementForm({
        type: "update",
        title: "",
        message: "",
        cta_text: "",
        cta_url: "",
        language: "fi",
        is_active: true,
      });
      fetchAnnouncements();
    } catch (error) {
      toast.error("Julkaisu epäonnistui");
    }
  };

  const handleToggleAnnouncement = async (id, currentActive) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/announcements/${id}`,
        { is_active: !currentActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAnnouncements();
    } catch (e) {
      toast.error("Päivitys epäonnistui");
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm("Poistetaanko tämä ilmoitus pysyvästi?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ilmoitus poistettu");
      fetchAnnouncements();
    } catch (e) {
      toast.error("Poisto epäonnistui");
    }
  };

  const handleGenerateStyleSamples = async (force = false) => {
    const confirmMsg = force
      ? "Pakotetaanko ALL-tyylien uudelleengenerointi? Tämä kuluttaa Nano Banana -budjettia 6 kuvan verran."
      : "Generoidaanko AI-taidetyylien esimerkkikuvat (6 kpl, vain puuttuvat)? Tämä voi kestää ~1-2 min.";
    if (!window.confirm(confirmMsg)) return;
    setStylesGenerating(true);
    setStylesResults(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/artwork-style-samples/generate`,
        { force },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
      );
      setStylesResults(res.data?.results || []);
      const generated = (res.data?.results || []).filter((r) => r.status === "generated").length;
      const cached = (res.data?.results || []).filter((r) => r.status === "cached").length;
      const errors = (res.data?.results || []).filter((r) => r.status === "error").length;
      toast.success(`Valmis: ${generated} generoitu, ${cached} välimuistissa, ${errors} virhettä`);
    } catch (e) {
      toast.error(`Generointi epäonnistui: ${e.response?.data?.detail || e.message}`);
    } finally {
      setStylesGenerating(false);
    }
  };

  const fetchFeedback = async (rating = null) => {
    try {
      const token = localStorage.getItem('token');
      const params = rating ? `?rating=${rating}` : "";
      const response = await axios.get(`${API}/admin/feedback${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(response.data.items || []);
      setFeedbackStats(response.data.stats || { total: 0, average_rating: 0, by_rating: {} });
    } catch (error) {
      console.error("Failed to fetch feedback");
    }
  };

  const handleDeleteFeedback = async (id) => {
    if (!window.confirm("Poistetaanko tämä palaute?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/feedback/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Palaute poistettu");
      fetchFeedback(feedbackFilter === "all" ? null : parseInt(feedbackFilter));
    } catch (error) {
      toast.error("Poisto epäonnistui");
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Vain admin-käyttäjille");
        navigate('/dashboard');
      } else {
        toast.error("Virhe ladattaessa käyttäjiä");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data);
    } catch (error) {
      console.error("Failed to fetch comments");
    }
  };

  const fetchCircles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/circles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCircles(response.data);
    } catch (error) {
      console.error("Failed to fetch circles");
    }
  };

  const fetchCircleDreams = async (circleId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/circles/${circleId}/dreams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCircleDreams(response.data);
    } catch (error) {
      toast.error("Virhe ladattaessa piirin unia");
    }
  };

  const fetchTrialCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/free-trials/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrialCount(response.data.count);
    } catch {}
  };

  const handleClearTrials = async () => {
    if (!window.confirm("Tyhjennä kaikki ilmaiset kokeilut?")) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/admin/free-trials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${response.data.deleted} kokeilua tyhjennetty!`);
      setTrialCount(0);
    } catch {
      toast.error("Virhe tyhjentäessä kokeiluja");
    }
  };


  const handleDeleteCircle = async (circleId, circleName) => {
    if (!window.confirm(`Haluatko varmasti poistaa unipiirin "${circleName}" ja kaikki sen jaetut unet?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/circles/${circleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Unipiiri poistettu!");
      setCircles(circles.filter(c => c.id !== circleId));
      if (selectedCircle?.id === circleId) {
        setSelectedCircle(null);
        setCircleDreams([]);
      }
    } catch (error) {
      toast.error("Virhe poistettaessa piiriä");
    }
  };

  const handleDeleteCircleDream = async (circleId, dreamId) => {
    if (!window.confirm("Haluatko varmasti poistaa tämän unen piiristä?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/circles/${circleId}/dreams/${dreamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Uni poistettu piiristä!");
      setCircleDreams(circleDreams.filter(d => d.dream_id !== dreamId));
    } catch (error) {
      toast.error("Virhe poistettaessa unta");
    }
  };


  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Haluatko varmasti poistaa käyttäjän ${email}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Käyttäjä ${email} poistettu!`);
      setUsers(users.filter(u => u.email !== email));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Virhe poistettaessa");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Haluatko varmasti poistaa tämän kommentin?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Kommentti poistettu!");
      setComments(comments.filter(c => c.id !== commentId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Virhe poistettaessa");
    }
  };

  const handleTogglePremium = async (email, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/users/${encodeURIComponent(email)}/premium?is_premium=${!currentStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Premium ${!currentStatus ? 'aktivoitu' : 'poistettu'}: ${email}`);
      setUsers(users.map(u => 
        u.email === email ? { ...u, is_premium: !currentStatus } : u
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Virhe");
    }
  };

  const fetchBlogPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/blog/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogPosts(response.data);
    } catch (error) {
      console.error("Failed to fetch blog posts");
    }
  };

  const handleSeedBlog = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/blog/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchBlogPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Virhe");
    }
  };

  const handleSaveBlogPost = async () => {
    try {
      const token = localStorage.getItem('token');
      const postData = {
        ...blogForm,
        tags: blogForm.tags.split(",").map(t => t.trim()).filter(Boolean)
      };

      if (editingPost) {
        await axios.patch(`${API}/blog/${editingPost.id}`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Artikkeli päivitetty!");
      } else {
        await axios.post(`${API}/blog`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Artikkeli luotu!");
      }
      setShowBlogForm(false);
      setEditingPost(null);
      setBlogForm({ title: "", content: "", excerpt: "", cover_image: "", tags: "", language: "fi", is_published: false });
      fetchBlogPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Virhe tallennettaessa");
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setBlogForm({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || "",
      cover_image: post.cover_image || "",
      tags: (post.tags || []).join(", "),
      language: post.language || "fi",
      is_published: post.is_published
    });
    setShowBlogForm(true);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Haluatko varmasti poistaa tämän artikkelin?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Artikkeli poistettu!");
      setBlogPosts(blogPosts.filter(p => p.id !== postId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Virhe poistettaessa");
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/blog/${post.id}`, { is_published: !post.is_published }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(post.is_published ? "Artikkeli piilotettu" : "Artikkeli julkaistu!");
      setBlogPosts(blogPosts.map(p => p.id === post.id ? {...p, is_published: !p.is_published} : p));
    } catch (error) {
      toast.error("Virhe");
    }
  };

  const handleTranslatePost = async (post) => {
    const allLangs = ["fi", "en", "fr", "de", "es"];
    const targetLangs = allLangs.filter(l => l !== post.language);
    if (!window.confirm(`Käännetään "${post.title}" kielille: ${targetLangs.join(", ").toUpperCase()}. Luonnoksiksi ennen julkaisua. Jatketaanko?`)) return;
    setTranslating(post.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/blog/translate/${post.slug}`, {
        target_languages: targetLangs
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      toast.success(`${response.data.created} käännöstä luotu luonnoksiksi!`);
      if (response.data.errors?.length) {
        toast.error(`${response.data.errors.length} virhettä: ${response.data.errors[0]}`);
      }
      fetchBlogPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Käännös epäonnistui");
    } finally {
      setTranslating(null);
    }
  };

  const handleAiGenerate = async () => {
    setAiGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/blog/generate`, {
        topic: aiTopic || null,
        language: blogForm.language
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      setBlogForm({
        ...blogForm,
        title: response.data.title,
        content: response.data.content,
        excerpt: response.data.excerpt,
        tags: (response.data.tags || []).join(", ")
      });
      setAiTopic("");
      toast.success("Artikkeli generoitu tekoälyllä!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "AI-generointi epäonnistui");
    } finally {
      setAiGenerating(false);
    }
  };

  const handleBatchGenerate = async () => {
    const total = batchCount * batchLanguages.length;
    if (!window.confirm(`Generoidaan ${batchCount} aihetta × ${batchLanguages.length} kieltä = ${total} artikkelia. Jatketaanko?`)) return;
    setBatchGenerating(true);
    setBatchProgress({ current: 0, total, currentTitle: "" });

    const token = localStorage.getItem('token');
    let created = 0;
    let errors = 0;

    const topics = [
      "dream symbols and their meanings",
      "how to remember dreams better",
      "lucid dreaming guide for beginners",
      "why we have nightmares and how to stop them",
      "the science of sleep cycles and dreams",
      "recurring dreams and what they mean",
      "dream journaling benefits for mental health",
      "history of dream interpretation",
      "how stress affects your dreams",
      "foods that affect your dreams",
      "children's dreams and nightmares",
      "dream incubation techniques",
      "prophetic dreams - science or myth",
      "sleep paralysis explained",
      "how to use dreams for creative problem solving",
    ];

    for (let i = 0; i < Math.min(batchCount, topics.length); i++) {
      for (const lang of batchLanguages) {
        const current = created + errors + 1;
        setBatchProgress({ current, total, currentTitle: `${topics[i]} (${lang.toUpperCase()})` });
        try {
          const response = await axios.post(`${API}/admin/blog/generate`, {
            topic: topics[i],
            language: lang
          }, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 60000
          });

          if (response.data?.title) {
            // Save the generated article
            await axios.post(`${API}/blog`, {
              title: response.data.title,
              content: response.data.content,
              excerpt: response.data.excerpt,
              tags: response.data.tags || [],
              language: lang,
              is_published: true
            }, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 15000
            });
            created++;
          }
        } catch (err) {
          errors++;
          console.error(`Failed: ${topics[i]} (${lang})`, err);
        }
      }
    }

    toast.success(`${created} artikkelia luotu!${errors > 0 ? ` (${errors} virhettä)` : ''}`);
    setBatchGenerating(false);
    setBatchProgress({ current: 0, total: 0, currentTitle: "" });
    fetchBlogPosts();
  };

  const toggleBatchLanguage = (lang) => {
    setBatchLanguages(prev => 
      prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]
    );
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredComments = comments.filter(c =>
    c.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gradient-dreamy">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-void hover:text-void/70">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <img src="/logo-new.png" alt="Uneksia" className="w-12 h-12" />
                <span className="font-heading text-xl text-void">Admin</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSelector variant="minimal" />
              <Button
                variant="ghost"
                onClick={onLogout}
                className="text-void hover:text-void/70 rounded-full"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="glass-effect rounded-3xl p-8">
          {/* Tabs */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setActiveTab("users")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-body transition-all ${
                activeTab === "users" 
                  ? "bg-void text-white" 
                  : "bg-white/50 text-void hover:bg-white/70"
              }`}
            >
              <Users className="w-5 h-5" />
              Käyttäjät ({users.length})
            </button>
            <button
              onClick={() => setActiveTab("comments")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-body transition-all ${
                activeTab === "comments" 
                  ? "bg-void text-white" 
                  : "bg-white/50 text-void hover:bg-white/70"
              }`}
            >
              <MessageSquare className="w-5 h-5" />
              Kommentit ({comments.length})
            </button>
            <button
              onClick={() => setActiveTab("blog")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-body transition-all ${
                activeTab === "blog" 
                  ? "bg-void text-white" 
                  : "bg-white/50 text-void hover:bg-white/70"
              }`}
              data-testid="admin-blog-tab"
            >
              <FileText className="w-5 h-5" />
              {t('blog.title')} ({blogPosts.length})
            </button>
            <button
              onClick={() => setActiveTab("circles")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-body transition-all ${
                activeTab === "circles" 
                  ? "bg-void text-white" 
                  : "bg-white/50 text-void hover:bg-white/70"
              }`}
              data-testid="admin-circles-tab"
            >
              <Users className="w-5 h-5" />
              Unipiirit ({circles.length})
            </button>
            <button
              onClick={() => setActiveTab("feedback")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-body transition-all ${
                activeTab === "feedback"
                  ? "bg-void text-white"
                  : "bg-white/50 text-void hover:bg-white/70"
              }`}
              data-testid="admin-feedback-tab"
            >
              <FeedbackIcon className="w-5 h-5" />
              Palautteet ({feedbackStats.total})
            </button>
            <button
              onClick={() => setActiveTab("announcements")}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-body transition-all ${
                activeTab === "announcements"
                  ? "bg-void text-white"
                  : "bg-white/50 text-void hover:bg-white/70"
              }`}
              data-testid="admin-announcements-tab"
            >
              <Megaphone className="w-5 h-5" />
              Ilmoitukset ({announcements.filter(a => a.is_active).length})
            </button>
          </div>

          {/* Search */}
          <div className="mb-6">
            <Input
              type="text"
              placeholder={activeTab === "users" ? "Hae sähköpostilla tai nimellä..." : "Hae kommenteista..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-white/50 border-transparent focus:border-ethereal rounded-xl"
            />
          </div>

          {/* Users Tab */}
          {activeTab === "users" && (
            <>
              {/* Free Trial Management */}
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <button
                  onClick={handleClearTrials}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-700 hover:bg-orange-100 rounded-full font-body text-sm transition-all"
                  data-testid="admin-clear-trials"
                >
                  <Trash2 className="w-4 h-4" />
                  Tyhjennä kokeilut ({trialCount})
                </button>
                <button
                  onClick={() => handleGenerateStyleSamples(false)}
                  disabled={stylesGenerating}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-full font-body text-sm transition-all disabled:opacity-60"
                  data-testid="admin-generate-style-samples"
                >
                  {stylesGenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ImageIcon className="w-4 h-4" />
                  )}
                  {stylesGenerating
                    ? "Generoidaan esimerkkejä..."
                    : "Generoi AI-tyylien esimerkit"}
                </button>
                <button
                  onClick={() => handleGenerateStyleSamples(true)}
                  disabled={stylesGenerating}
                  className="flex items-center gap-2 px-4 py-2 bg-rose-50 text-rose-700 hover:bg-rose-100 rounded-full font-body text-sm transition-all disabled:opacity-60"
                  data-testid="admin-regenerate-style-samples"
                  title="Pakottaa kaikkien 6 tyylin uudelleengeneroinnin"
                >
                  <Wand2 className="w-4 h-4" />
                  Pakota regen
                </button>
              </div>
              {stylesResults && (
                <div className="mb-4 text-xs text-void/60 font-body bg-white/40 rounded-xl px-3 py-2" data-testid="admin-style-samples-result">
                  {stylesResults.map((r) => (
                    <span key={r.key} className="mr-3">
                      {r.key}: <span className={
                        r.status === "generated" ? "text-green-600" :
                        r.status === "cached" ? "text-void/50" :
                        "text-red-600"
                      }>{r.status}</span>
                    </span>
                  ))}
                </div>
              )}
              {loading ? (
                <p className="text-void/60 text-center py-8">Ladataan...</p>
              ) : (
                <div className="space-y-3">
                  {filteredUsers.length === 0 ? (
                    <p className="text-void/60 text-center py-8">Ei käyttäjiä</p>
                  ) : (
                    filteredUsers.map((u) => (
                      <div 
                        key={u.id} 
                        className="flex items-center justify-between bg-white/50 rounded-xl p-4"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-body text-void font-medium">{u.name}</p>
                            {u.is_premium && (
                              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                u.premium_type === 'yearly'
                                  ? 'bg-amber-100 text-amber-800'
                                  : u.premium_type === 'monthly'
                                  ? 'bg-indigo-100 text-indigo-800'
                                  : 'bg-emerald-100 text-emerald-800'
                              }`}>
                                {u.premium_type === 'yearly' ? 'Ostettu (Vuosi)' 
                                  : u.premium_type === 'monthly' ? 'Ostettu (Kk)'
                                  : 'Annettu'}
                              </span>
                            )}
                            {!u.is_premium && u.subscription_cancelled && (
                              <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-red-100 text-red-700">
                                Peruttu
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-void/60 font-body">{u.email}</p>
                          {u.is_premium && u.stripe_customer_id && (
                            <p className="text-xs text-void/40 font-body mt-1">Stripe: {u.stripe_customer_id}</p>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleTogglePremium(u.email, u.is_premium)}
                            variant="outline"
                            size="sm"
                            className={`rounded-full ${u.is_premium ? 'border-lucid text-lucid' : 'border-void/20'}`}
                            title={u.is_premium ? 'Poista Premium' : 'Anna Premium'}
                          >
                            <Crown className="w-4 h-4" />
                          </Button>
                          
                          {u.email !== 'annelelouarn1@outlook.com' && (
                            <Button
                              onClick={() => handleDeleteUser(u.email)}
                              variant="outline"
                              size="sm"
                              className="rounded-full border-red-300 text-red-500 hover:bg-red-50"
                              title="Poista käyttäjä"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}

          {/* Comments Tab */}
          {activeTab === "comments" && (
            <div className="space-y-3">
              {filteredComments.length === 0 ? (
                <p className="text-void/60 text-center py-8">Ei kommentteja</p>
              ) : (
                filteredComments.map((comment) => (
                  <div 
                    key={comment.id} 
                    className="bg-white/50 rounded-xl p-4"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-body text-void font-medium">{comment.user_name}</p>
                          <span className="text-xs text-void/40">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="text-void/80 font-body">{comment.content}</p>
                      </div>
                      
                      <Button
                        onClick={() => handleDeleteComment(comment.id)}
                        variant="outline"
                        size="sm"
                        className="rounded-full border-red-300 text-red-500 hover:bg-red-50 ml-4"
                        title="Poista kommentti"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Blog Tab */}
          {activeTab === "blog" && (
            <div className="space-y-4">
              {!showBlogForm ? (
                <>
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Button
                      onClick={() => { setShowBlogForm(true); setEditingPost(null); setBlogForm({ title: "", content: "", excerpt: "", cover_image: "", tags: "", language: "fi", is_published: false }); }}
                      className="bg-void text-white hover:bg-void/90 rounded-full px-4 py-2 font-body text-sm"
                      data-testid="admin-new-blog-post"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {t('blog.newPost')}
                    </Button>
                    {blogPosts.length === 0 && (
                      <Button
                        onClick={handleSeedBlog}
                        variant="outline"
                        className="rounded-full px-4 py-2 font-body text-sm border-void/20"
                        data-testid="admin-seed-blog"
                      >
                        <Sparkles className="w-4 h-4 mr-1" />
                        {t('blog.seedPosts')}
                      </Button>
                    )}
                  </div>

                  {/* AI Batch Generator */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("token");
                          const res = await axios.post(`${API}/admin/blog/auto-assign-images`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          const data = res.data;
                          if (data.images_assigned > 0 || data.authors_fixed > 0) {
                            toast.success(`Kuvat lisätty ${data.images_assigned} artikkeliin, kirjoittaja korjattu ${data.authors_fixed} artikkelissa`);
                            fetchBlogPosts();
                          } else {
                            toast.info("Kaikilla artikkeleilla on jo kuvat");
                          }
                        } catch (err) {
                          toast.error("Kuvien lisäys epäonnistui");
                        }
                      }}
                      variant="outline"
                      className="rounded-full px-4 py-2 font-body text-sm border-purple-200 text-purple-700 hover:bg-purple-50"
                      data-testid="auto-assign-images-btn"
                    >
                      <ImageIcon className="w-4 h-4 mr-1" />
                      Lisää kuvat automaattisesti
                    </Button>
                  </div>
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-5 border border-purple-100">
                    <h4 className="font-heading text-void text-lg mb-3 flex items-center gap-2">
                      <Wand2 className="w-5 h-5 text-purple-500" />
                      AI-artikkeligenerointi
                    </h4>
                    <p className="text-sm text-void/60 font-body mb-3">Generoi useita SEO-optimoituja artikkeleita tekoälyllä eri kielillä</p>
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-body text-void">Määrä:</label>
                        <select
                          value={batchCount}
                          onChange={(e) => setBatchCount(parseInt(e.target.value))}
                          className="bg-white/80 border border-purple-200 rounded-lg px-2 py-1 text-sm font-body"
                          data-testid="batch-count-select"
                        >
                          {[1,2,3,5,8,10,15].map(n => (
                            <option key={n} value={n}>{n} aihetta</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-1">
                        <Languages className="w-4 h-4 text-void/60" />
                        {["fi","en","fr","de","es"].map(lang => (
                          <button
                            key={lang}
                            onClick={() => toggleBatchLanguage(lang)}
                            className={`px-2 py-1 text-xs rounded-full font-body transition-all ${
                              batchLanguages.includes(lang) 
                                ? 'bg-void text-white' 
                                : 'bg-white/60 text-void/40 border border-void/10'
                            }`}
                            data-testid={`batch-lang-${lang}`}
                          >
                            {lang.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleBatchGenerate}
                        disabled={batchGenerating || batchLanguages.length === 0}
                        className="bg-purple-600 text-white hover:bg-purple-700 rounded-full px-5 py-2 font-body text-sm disabled:opacity-50"
                        data-testid="batch-generate-btn"
                      >
                        {batchGenerating ? (
                          <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {batchProgress.current}/{batchProgress.total}...</>
                        ) : (
                          <><Wand2 className="w-4 h-4 mr-1" /> Generoi {batchCount * batchLanguages.length} artikkelia</>
                        )}
                      </Button>
                      {batchGenerating && (
                        <div className="flex-1 ml-2">
                          <div className="w-full bg-purple-100 rounded-full h-2 mb-1">
                            <div
                              className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${batchProgress.total > 0 ? (batchProgress.current / batchProgress.total) * 100 : 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-void/50 font-body truncate block">{batchProgress.currentTitle}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Blog Post List */}
                  {blogPosts.length === 0 ? (
                    <p className="text-void/60 text-center py-8">Ei artikkeleita</p>
                  ) : (
                    blogPosts.map((post) => (
                      <div key={post.id} className="bg-white/50 rounded-xl p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-body text-void font-medium">{post.title}</p>
                              <span className={`px-2 py-0.5 text-xs rounded-full font-body ${
                                post.is_published ? 'bg-lucid/20 text-green-700' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {post.is_published ? t('blog.published') : t('blog.draft')}
                              </span>
                              <span className="px-2 py-0.5 bg-ethereal/20 text-void/60 text-xs rounded-full">
                                {post.language?.toUpperCase()}
                              </span>
                            </div>
                            <p className="text-sm text-void/60 font-body line-clamp-1">{post.excerpt}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-void/40">
                              <span>{formatDate(post.created_at)}</span>
                              <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{post.views || 0}</span>
                            </div>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <Button
                              onClick={() => handleTogglePublish(post)}
                              variant="outline"
                              size="sm"
                              className="rounded-full border-void/20"
                              title={post.is_published ? t('blog.unpublish') : t('blog.publish')}
                            >
                              {post.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              onClick={() => handleTranslatePost(post)}
                              variant="outline"
                              size="sm"
                              className="rounded-full border-void/20"
                              title="Käännä muille kielille"
                              disabled={translating === post.id}
                              data-testid={`translate-${post.slug}`}
                            >
                              {translating === post.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
                            </Button>
                            <Button
                              onClick={() => handleEditPost(post)}
                              variant="outline"
                              size="sm"
                              className="rounded-full border-void/20"
                              title={t('blog.editPost')}
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeletePost(post.id)}
                              variant="outline"
                              size="sm"
                              className="rounded-full border-red-300 text-red-500 hover:bg-red-50"
                              title={t('blog.deletePost')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <h3 className="font-heading text-xl text-void">
                    {editingPost ? t('blog.editPost') : t('blog.newPost')}
                  </h3>

                  {/* AI Generate Section */}
                  {!editingPost && (
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-100">
                      <p className="text-sm font-body text-void/70 mb-2 flex items-center gap-1">
                        <Wand2 className="w-4 h-4 text-purple-500" />
                        Generoi tekoälyllä
                      </p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Aihe (valinnainen, esim. 'selkounet', 'painajaiset')"
                          value={aiTopic}
                          onChange={(e) => setAiTopic(e.target.value)}
                          className="bg-white/80 border-purple-200 rounded-lg flex-1"
                          data-testid="ai-topic-input"
                        />
                        <Button
                          onClick={handleAiGenerate}
                          disabled={aiGenerating}
                          className="bg-purple-600 text-white hover:bg-purple-700 rounded-lg px-4 disabled:opacity-50"
                          data-testid="ai-generate-btn"
                        >
                          {aiGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  )}

                  <Input
                    placeholder={t('blog.postTitle')}
                    value={blogForm.title}
                    onChange={(e) => setBlogForm({...blogForm, title: e.target.value})}
                    className="bg-white/50 border-transparent focus:border-ethereal rounded-xl"
                    data-testid="blog-form-title"
                  />
                  <Input
                    placeholder={t('blog.postExcerpt')}
                    value={blogForm.excerpt}
                    onChange={(e) => setBlogForm({...blogForm, excerpt: e.target.value})}
                    className="bg-white/50 border-transparent focus:border-ethereal rounded-xl"
                    data-testid="blog-form-excerpt"
                  />
                  <textarea
                    placeholder={t('blog.postContent')}
                    value={blogForm.content}
                    onChange={(e) => setBlogForm({...blogForm, content: e.target.value})}
                    className="w-full h-64 bg-white/50 border border-transparent focus:border-purple-200 rounded-xl p-3 font-body text-void resize-y focus:outline-none focus:ring-2 focus:ring-purple-100/50"
                    data-testid="blog-form-content"
                  />
                  <Input
                    placeholder={t('blog.postCoverImage')}
                    value={blogForm.cover_image}
                    onChange={(e) => setBlogForm({...blogForm, cover_image: e.target.value})}
                    className="bg-white/50 border-transparent focus:border-ethereal rounded-xl"
                    data-testid="blog-form-cover"
                  />
                  <Input
                    placeholder={t('blog.postTags')}
                    value={blogForm.tags}
                    onChange={(e) => setBlogForm({...blogForm, tags: e.target.value})}
                    className="bg-white/50 border-transparent focus:border-ethereal rounded-xl"
                    data-testid="blog-form-tags"
                  />
                  <div className="flex gap-4 items-center">
                    <select
                      value={blogForm.language}
                      onChange={(e) => setBlogForm({...blogForm, language: e.target.value})}
                      className="bg-white/50 border border-transparent focus:border-purple-200 rounded-xl px-3 py-2 font-body text-void focus:outline-none"
                      data-testid="blog-form-language"
                    >
                      <option value="fi">Suomi</option>
                      <option value="en">English</option>
                      <option value="fr">Français</option>
                      <option value="de">Deutsch</option>
                      <option value="es">Español</option>
                    </select>
                    <label className="flex items-center gap-2 font-body text-void cursor-pointer">
                      <input
                        type="checkbox"
                        checked={blogForm.is_published}
                        onChange={(e) => setBlogForm({...blogForm, is_published: e.target.checked})}
                        className="w-4 h-4 accent-void"
                        data-testid="blog-form-publish-checkbox"
                      />
                      {t('blog.publish')}
                    </label>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleSaveBlogPost}
                      className="bg-void text-white hover:bg-void/90 rounded-full px-6 py-2 font-body"
                      data-testid="blog-form-save"
                    >
                      {t('blog.savePost')}
                    </Button>
                    <Button
                      onClick={() => { setShowBlogForm(false); setEditingPost(null); }}
                      variant="outline"
                      className="rounded-full px-6 py-2 font-body border-void/20"
                      data-testid="blog-form-cancel"
                    >
                      {t('blog.cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Circles Tab */}
          {activeTab === "circles" && (
            <div className="space-y-4">
              {selectedCircle ? (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Button
                      variant="ghost"
                      onClick={() => { setSelectedCircle(null); setCircleDreams([]); }}
                      className="rounded-full"
                      data-testid="circles-back-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Takaisin
                    </Button>
                    <h3 className="font-heading text-xl text-void">{selectedCircle.name}</h3>
                    <span className="text-sm text-void/50 font-body">
                      {selectedCircle.member_count} jäsentä | {circleDreams.length} unta
                    </span>
                  </div>
                  <p className="text-sm text-void/60 font-body mb-4">{selectedCircle.description}</p>
                  
                  {circleDreams.length === 0 ? (
                    <p className="text-void/50 text-center py-8 font-body">Ei jaettuja unia tässä piirissä</p>
                  ) : (
                    <div className="space-y-3">
                      {circleDreams.map((dream) => (
                        <div key={dream.dream_id} className="glass-effect rounded-2xl p-4" data-testid={`circle-dream-${dream.dream_id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-body font-medium text-void">{dream.user_name}</span>
                                <span className="text-xs text-void/40 font-body">
                                  {dream.created_at ? new Date(dream.created_at).toLocaleDateString('fi-FI') : ''}
                                </span>
                              </div>
                              <h4 className="font-heading text-lg text-void mb-1">{dream.dream_title}</h4>
                              <p className="text-sm text-void/70 font-body line-clamp-3">{dream.dream_content?.substring(0, 200)}...</p>
                              {dream.interpretations?.length > 0 && (
                                <p className="text-xs text-void/50 font-body mt-2">
                                  {dream.interpretations.length} tulkintaa
                                </p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              onClick={() => handleDeleteCircleDream(selectedCircle.id, dream.dream_id)}
                              className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full p-2 ml-2"
                              data-testid={`delete-circle-dream-${dream.dream_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {circles.length === 0 ? (
                    <p className="text-void/50 text-center py-8 font-body">Ei unipirejä vielä</p>
                  ) : (
                    <div className="space-y-3">
                      {circles.map((circle) => (
                        <div key={circle.id} className="glass-effect rounded-2xl p-4 flex items-center justify-between" data-testid={`admin-circle-${circle.id}`}>
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() => { setSelectedCircle(circle); fetchCircleDreams(circle.id); }}
                          >
                            <div className="flex items-center gap-2">
                              <h4 className="font-heading text-lg text-void">{circle.name}</h4>
                              {circle.is_private && (
                                <span className="text-xs px-2 py-0.5 bg-void/10 text-void/60 rounded-full font-body">Yksityinen</span>
                              )}
                            </div>
                            <p className="text-sm text-void/60 font-body">{circle.description?.substring(0, 100)}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-void/40 font-body">
                              <span>{circle.member_count || circle.member_ids?.length || 0} jäsentä</span>
                              <span>{circle.dream_count || 0} unta</span>
                              <span>Luoja: {circle.creator_name}</span>
                              <span>{circle.created_at ? new Date(circle.created_at).toLocaleDateString('fi-FI') : ''}</span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            onClick={() => handleDeleteCircle(circle.id, circle.name)}
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full p-2 ml-2"
                            data-testid={`delete-circle-${circle.id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Feedback Tab */}
          {activeTab === "feedback" && (
            <div data-testid="admin-feedback-content">
              {/* Stats cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="glass-effect rounded-2xl p-4">
                  <p className="text-xs text-void/60 font-body uppercase tracking-wide">Yhteensä</p>
                  <p className="text-3xl font-heading font-light text-void mt-1">{feedbackStats.total}</p>
                </div>
                <div className="glass-effect rounded-2xl p-4">
                  <p className="text-xs text-void/60 font-body uppercase tracking-wide">Keskiarvo</p>
                  <p className="text-3xl font-heading font-light text-void mt-1 flex items-center gap-1">
                    {feedbackStats.average_rating || 0}
                    <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  </p>
                </div>
                <div className="glass-effect rounded-2xl p-4">
                  <p className="text-xs text-void/60 font-body uppercase tracking-wide">5 tähteä</p>
                  <p className="text-3xl font-heading font-light text-emerald-600 mt-1">
                    {feedbackStats.by_rating?.["5"] || 0}
                  </p>
                </div>
                <div className="glass-effect rounded-2xl p-4">
                  <p className="text-xs text-void/60 font-body uppercase tracking-wide">1-2 tähteä</p>
                  <p className="text-3xl font-heading font-light text-rose-600 mt-1">
                    {(feedbackStats.by_rating?.["1"] || 0) + (feedbackStats.by_rating?.["2"] || 0)}
                  </p>
                </div>
              </div>

              {/* Rating filter */}
              <div className="flex flex-wrap gap-2 mb-4">
                {["all", "5", "4", "3", "2", "1"].map((r) => (
                  <button
                    key={r}
                    onClick={() => {
                      setFeedbackFilter(r);
                      fetchFeedback(r === "all" ? null : parseInt(r));
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-body transition-all ${
                      feedbackFilter === r
                        ? "bg-void text-white"
                        : "bg-white/50 text-void hover:bg-white/70"
                    }`}
                    data-testid={`feedback-filter-${r}`}
                  >
                    {r === "all" ? "Kaikki" : (
                      <span className="inline-flex items-center gap-1">
                        {r} <Star className="w-3 h-3 fill-current" />
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Feedback list */}
              {feedback.length === 0 ? (
                <div className="text-center py-12 glass-effect rounded-2xl">
                  <FeedbackIcon className="w-12 h-12 text-void/30 mx-auto mb-3" />
                  <p className="text-void/60 font-body">Ei palautteita {feedbackFilter !== "all" ? `(${feedbackFilter} tähden suodatus)` : ""}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {feedback.map((fb) => (
                    <div
                      key={fb.id}
                      className="glass-effect rounded-2xl p-5 hover:shadow-md transition-all"
                      data-testid={`feedback-item-${fb.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <div className="flex items-center" data-testid="feedback-rating">
                              {[1, 2, 3, 4, 5].map((i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i <= (fb.rating || 0)
                                      ? "text-amber-500 fill-amber-500"
                                      : "text-void/20"
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-sm font-body font-medium text-void">
                              {fb.name || "Anonyymi"}
                            </span>
                            {fb.email && (
                              <a
                                href={`mailto:${fb.email}`}
                                className="text-xs text-purple-600 hover:underline font-body"
                                data-testid="feedback-email-link"
                              >
                                {fb.email}
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-void/40 font-body">
                            <span>
                              {fb.created_at
                                ? new Date(fb.created_at).toLocaleString("fi-FI", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""}
                            </span>
                            {fb.page && (
                              <span className="px-2 py-0.5 rounded-full bg-void/5">
                                {fb.page}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          onClick={() => handleDeleteFeedback(fb.id)}
                          className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full p-2"
                          data-testid={`delete-feedback-${fb.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-void/80 font-body whitespace-pre-wrap text-sm">
                        {fb.message}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Announcements Tab */}
          {activeTab === "announcements" && (
            <div data-testid="admin-announcements-content">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-void/60 font-body">
                  Lähetä ilmoituksia kaikille käyttäjille (uutuudet, palautekyselyt, markkinointi)
                </p>
                <Button
                  onClick={() => setShowAnnouncementForm(!showAnnouncementForm)}
                  className="bg-void text-white hover:bg-void/90 rounded-full"
                  data-testid="new-announcement-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {showAnnouncementForm ? "Sulje" : "Uusi ilmoitus"}
                </Button>
              </div>

              {showAnnouncementForm && (
                <form
                  onSubmit={handleCreateAnnouncement}
                  className="glass-effect rounded-2xl p-5 mb-6 space-y-4"
                  data-testid="announcement-form"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-body text-void/60 mb-1 uppercase tracking-wide">Tyyppi</label>
                      <select
                        value={announcementForm.type}
                        onChange={(e) => setAnnouncementForm({ ...announcementForm, type: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-void/20 bg-white/80 font-body text-sm"
                        data-testid="announcement-type-select"
                      >
                        <option value="update">🎉 Uutuus / päivitys</option>
                        <option value="feedback">💬 Palautekysely</option>
                        <option value="marketing">📣 Markkinointi</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-body text-void/60 mb-1 uppercase tracking-wide">Kieli</label>
                      <select
                        value={announcementForm.language}
                        onChange={(e) => setAnnouncementForm({ ...announcementForm, language: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-void/20 bg-white/80 font-body text-sm"
                        data-testid="announcement-language-select"
                      >
                        <option value="fi">🇫🇮 Suomi</option>
                        <option value="en">🇬🇧 English</option>
                        <option value="fr">🇫🇷 Français</option>
                        <option value="de">🇩🇪 Deutsch</option>
                        <option value="es">🇪🇸 Español</option>
                        <option value="all">🌍 Kaikki kielet</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 font-body text-sm">
                        <input
                          type="checkbox"
                          checked={announcementForm.is_active}
                          onChange={(e) => setAnnouncementForm({ ...announcementForm, is_active: e.target.checked })}
                          className="w-4 h-4"
                          data-testid="announcement-active-checkbox"
                        />
                        Aktiivinen heti
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-body text-void/60 mb-1 uppercase tracking-wide">Otsikko</label>
                    <input
                      type="text"
                      value={announcementForm.title}
                      onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                      placeholder="Esim. Uutuus! ✨ Streak-palkinnot"
                      maxLength={80}
                      className="w-full px-3 py-2 rounded-lg border border-void/20 bg-white/80 font-body"
                      data-testid="announcement-title-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-body text-void/60 mb-1 uppercase tracking-wide">Viesti</label>
                    <textarea
                      value={announcementForm.message}
                      onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
                      placeholder="Esim. Sovellusta on päivitetty. Lataa uusin versio nähdäksesi uudet streak-palkinnot ja AI-unitaiteen!"
                      maxLength={300}
                      rows={3}
                      className="w-full px-3 py-2 rounded-lg border border-void/20 bg-white/80 font-body text-sm"
                      data-testid="announcement-message-input"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-body text-void/60 mb-1 uppercase tracking-wide">Napin teksti (valinnainen)</label>
                      <input
                        type="text"
                        value={announcementForm.cta_text}
                        onChange={(e) => setAnnouncementForm({ ...announcementForm, cta_text: e.target.value })}
                        placeholder="Esim. Tutustu"
                        maxLength={30}
                        className="w-full px-3 py-2 rounded-lg border border-void/20 bg-white/80 font-body text-sm"
                        data-testid="announcement-cta-text"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-body text-void/60 mb-1 uppercase tracking-wide">Napin osoite (valinnainen)</label>
                      <input
                        type="text"
                        value={announcementForm.cta_url}
                        onChange={(e) => setAnnouncementForm({ ...announcementForm, cta_url: e.target.value })}
                        placeholder="/dashboard tai https://..."
                        className="w-full px-3 py-2 rounded-lg border border-void/20 bg-white/80 font-body text-sm"
                        data-testid="announcement-cta-url"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full"
                    data-testid="announcement-submit"
                  >
                    <Megaphone className="w-4 h-4 mr-2" />
                    Julkaise ilmoitus
                  </Button>
                </form>
              )}

              {announcements.length === 0 ? (
                <div className="text-center py-12 glass-effect rounded-2xl">
                  <Megaphone className="w-12 h-12 text-void/30 mx-auto mb-3" />
                  <p className="text-void/60 font-body">Ei vielä ilmoituksia. Luo ensimmäinen yllä!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {announcements.map((a) => {
                    const typeLabels = {
                      update: { label: "🎉 Uutuus", color: "bg-purple-100 text-purple-700" },
                      feedback: { label: "💬 Palaute", color: "bg-emerald-100 text-emerald-700" },
                      marketing: { label: "📣 Markkinointi", color: "bg-amber-100 text-amber-700" },
                    };
                    const typeInfo = typeLabels[a.type] || typeLabels.update;
                    return (
                      <div
                        key={a.id}
                        className={`glass-effect rounded-2xl p-5 transition-all ${a.is_active ? "" : "opacity-50"}`}
                        data-testid={`announcement-item-${a.id}`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-body ${typeInfo.color}`}>
                                {typeInfo.label}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-void/5 font-body uppercase">
                                {a.language === "all" ? "🌍" : a.language}
                              </span>
                              {a.is_active ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-body">● Aktiivinen</span>
                              ) : (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-void/10 text-void/60 font-body">○ Piilotettu</span>
                              )}
                              <span className="text-xs text-void/40 font-body">
                                {a.created_at
                                  ? new Date(a.created_at).toLocaleDateString("fi-FI", { month: "short", day: "numeric" })
                                  : ""}
                              </span>
                            </div>
                            <h4 className="font-body font-medium text-void mb-1">{a.title}</h4>
                            <p className="text-sm text-void/70 font-body whitespace-pre-wrap">{a.message}</p>
                            {a.cta_text && (
                              <p className="text-xs text-void/50 font-body mt-2 italic">
                                Nappi: <span className="font-medium">{a.cta_text}</span> → {a.cta_url || "(ei osoitetta)"}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <Button
                              variant="ghost"
                              onClick={() => handleToggleAnnouncement(a.id, a.is_active)}
                              className="text-xs rounded-full px-3"
                              data-testid={`toggle-announcement-${a.id}`}
                            >
                              {a.is_active ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                              {a.is_active ? "Piilota" : "Aktivoi"}
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => handleDeleteAnnouncement(a.id)}
                              className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full text-xs px-3"
                              data-testid={`delete-announcement-${a.id}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Poista
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <p className="text-sm text-void/40 mt-6 text-center">
            {activeTab === "users" && `Yhteensä ${users.length} käyttäjää`}
            {activeTab === "comments" && `Yhteensä ${comments.length} kommenttia`}
            {activeTab === "blog" && `Yhteensä ${blogPosts.length} artikkelia`}
            {activeTab === "circles" && `Yhteensä ${circles.length} unipiiriä`}
            {activeTab === "feedback" && `Yhteensä ${feedbackStats.total} palautetta`}
            {activeTab === "announcements" && `Yhteensä ${announcements.length} ilmoitusta (${announcements.filter(a => a.is_active).length} aktiivista)`}
          </p>
        </div>
      </div>
    </div>
  );
}
